import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActionContainer from '../../../../components/ActionContainer';
import Button from '../../../../components/Button';
import ConfirmCheckoutDisclaimer from '../../../../components/ConfirmCheckoutDisclaimer';
import ErrorMessage, { useError } from '../../../../components/ErrorMessage';
import ItemsInCart from '../../../../components/ItemsInCart';
import { useCartPricing } from '../../../../contexts/CartPricingContext';
import useHandlePurchase from '../../../../hooks/useHandlePurchase';
import useShippingOptions from '../../../../hooks/useShippingOptions';
import * as events from '../../../../lib/analytics/events';
import CheckoutContext from '../../../../lib/CheckoutContext';
import ThankYouLocationState from '../../../../models/ThankYouLocationState';
import * as types from '../../../../types';
import BillingLineItem from './BillingLineItem';
import styles from './Confirm.module.scss';
import PricingSummary from './PricingSummary';
import ShippingLineItem from './ShippingLineItem';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import { logInternalError } from '../../../../lib/errors';
import * as Sentry from '@sentry/react';
import { SUPPLEMENT_PREFIXES } from '../../../../views/ManageSubscription/Supplements/SplashPage';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import { inCartPrepaidMembershipCount } from '../../../../components/ItemsInCart/CouponLineItemV2';
import OllieWelcomeBoxImage from '../../../../assets/images/ollie-welcome-box.png';
import classNames from 'classnames';
import { supplementsProductIsSupportedInCountry, useLocale } from '../../../../contexts/LocaleContext';
import { ThreeDSecureAction } from '@recurly/react-recurly';
import FiRecurlyProvider from '../../../../components/FiRecurlyProvider';
import { RecurlyError, TokenPayload } from '@recurly/recurly-js';
import MarketingOptInCheckbox from '../../../../components/MarketingOptInCheckbox';
import { loadStripe } from '@stripe/stripe-js';

interface ConfirmSectionProps {
  email: string;
  selectedShippingOption?: types.IShippingOption;
}

function OllieWelcomeBox() {
  const { t } = useTranslation();
  return (
    <div className={styles.ollieWelcomeBox}>
      <div className={styles.ollieWelcomeBoxContainer}>
        <img className={styles.ollieWelcomeBoxImage} src={OllieWelcomeBoxImage} alt={t('ollie_welcome_box')} />
        <div className={styles.ollieWelcomeBoxContent}>
          <div className={styles.ollieWelcomeBoxTitle}>{t('ollie_starter_box')}</div>
          <div className={styles.ollieWelcomeBoxDescription}>
            {t('with_your_fi_membership_you_will_receive_a_coupon_for_2_weeks_of_free_dog_food_with_ollie_175_value')}
          </div>
        </div>
      </div>
      <div className={styles.ollieWelcomeBoxPrice}>{t('free_uppercase')}</div>
    </div>
  );
}

export default function ConfirmSection({ email }: ConfirmSectionProps) {
  const { t } = useTranslation();
  const history = useHistory<ThankYouLocationState>();
  const checkoutPaths = useCheckoutPaths();

  const { checkoutState, requiresShippingDetails, cart } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();

  const { shippingOptions } = useShippingOptions();
  const selectedShippingOption = useMemo(
    () => shippingOptions.find((option) => option.code === checkoutState.shippingCode),
    [shippingOptions, checkoutState.shippingCode],
  );
  const { country, currency } = useLocale();

  const { error, errorID, setError } = useError();

  const [threeDSecurePending, setThreeDSecurePending] = useState<boolean>(false);
  const [threeDSecureActionToken, setThreeDSecureActionToken] = useState<string | null>(null);
  const [stripeReady, setStripeReady] = useState(false);

  // Recurly.js's ThreeDSecureAction needs window.Stripe to be loaded for Stripe-gateway 3DS
  // challenges. On the StripeCheckoutPage, loadStripe is called by StripeElementsProvider, but
  // on the review page it may not be available. Load it when a 3DS token arrives.
  useEffect(() => {
    if (!threeDSecureActionToken) return;
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    const accountId = process.env.REACT_APP_STRIPE_ACCOUNT_ID;
    if (key && accountId) {
      loadStripe(key, { stripeAccount: accountId }).then(() => setStripeReady(true));
    } else {
      // no stripe config — render ThreeDSecureAction anyway (non-stripe gateways don't need it)
      setStripeReady(true);
    }
  }, [threeDSecureActionToken]);

  const errorCallback = useCallback(
    (errorMessage: string, token?: string) => {
      setError(errorMessage);
      if (token) {
        setThreeDSecureActionToken(token);
        setThreeDSecurePending(true);
        events.confirmation.threeDSecurePrompt();
      } else {
        events.confirmation.error(errorMessage);
      }
    },
    [setError],
  );

  const successCallback = useCallback(
    (
      orderedCart: types.Cart,
      orderedCartPricing: types.CartPricing,
      invoiceNumber: string,
      isReturningCustomer: boolean,
      isGWP: boolean,
      isVerizon: boolean,
    ) => {
      // Any failures in analytics shouldn't prevent the user from seeing the thank you page since we
      // successfully charged the card at this point.
      try {
        const cartItemsArray = Object.values(orderedCart.cartItems);

        events.confirmation.finalConfirm(cartItemsArray, orderedCartPricing);
        events.orderCompleted(
          orderedCart,
          orderedCartPricing,
          invoiceNumber,
          checkoutState.shippingAddress,
          checkoutState.billingInfo,
          {
            isReturningCustomer,
            isGWP,
            currency,
          },
        );
      } catch (err) {
        logInternalError(err);
      }

      const supplementInCart = Object.values(orderedCart.cartItems).some((item) =>
        SUPPLEMENT_PREFIXES.some((prefix) => item.lineItem.sku.toUpperCase().startsWith(prefix)),
      );
      const showSupplementsUpsell = !supplementInCart && !isVerizon && supplementsProductIsSupportedInCountry(country);
      if (showSupplementsUpsell) {
        // Show the supplements page
        history.replace(checkoutPaths.Supplements, {
          orderId: invoiceNumber,
          orderedCart,
          orderedCartPricing,
        });
      } else {
        history.replace(checkoutPaths.ThankYou, {
          orderId: invoiceNumber,
          orderedCart,
          orderedCartPricing,
        });
      }
    },
    [
      checkoutPaths.Supplements,
      checkoutPaths.ThankYou,
      checkoutState.billingInfo,
      checkoutState.shippingAddress,
      history,
      country,
      currency,
    ],
  );

  const showOllieWelcomeBox =
    inCartPrepaidMembershipCount(Object.values(cart.cartItems)) > 0 &&
    getFiGrowthBook().getFeatureValue('ollie-welcome-box', false);

  const { handlePurchase, purchaseMutationLoading } = useHandlePurchase({
    cartPricing,
    onError: errorCallback,
    onSuccess: successCallback,
  });

  const onThreeDSecureSuccess = (token: TokenPayload) => {
    if (getFiGrowthBook().getFeatureValue('alert-on-stripe-elements-3ds', true)) {
      Sentry.captureMessage('3DS challenge succeeded (purchase step)', {
        level: 'info',
        tags: {
          checkoutStep: 'confirm3DSecure',
          threeDSecureResult: 'success',
        },
      });
    }
    events.confirmation.threeDSecureSuccess();
    setThreeDSecurePending(false);
    handlePurchase(email, undefined, token.id);
  };

  const onThreeDSecureError = (e: RecurlyError) => {
    const isVendorLoadError = (e as any).code === '3ds-vendor-load-error';
    if (getFiGrowthBook().getFeatureValue('alert-on-stripe-elements-3ds', true)) {
      logInternalError(e, {
        fingerprint: ['{{ default }}', e.message],
        tags: {
          checkoutStep: 'confirm3DSecure',
          threeDSecureResult: 'error',
          ...(isVendorLoadError && { threeDSecureVendorLoadError: 'true' }),
        },
      });
    }
    events.confirmation.threeDSecureError(e.message);
    setError(e.message);
    setThreeDSecurePending(false);
  };

  return (
    <div className={classNames(styles.main)}>
      <div className={styles.cartItemsContainer}>
        <div className={styles.cartItems}>
          <ItemsInCart defaultExpanded={false} />
          {showOllieWelcomeBox && (
            <div className={styles.ollieWelcomeBoxContainer}>
              <OllieWelcomeBox />
            </div>
          )}
          {selectedShippingOption && checkoutState.shippingAddress && requiresShippingDetails && (
            <ShippingLineItem
              customerName={checkoutState.customerName}
              shippingAddress={checkoutState.shippingAddress}
              shippingOption={selectedShippingOption}
            />
          )}
          {checkoutState.billingInfo && <BillingLineItem billingInfo={checkoutState.billingInfo} />}
        </div>
      </div>

      <div className={styles.summaryContainer}>
        <PricingSummary selectedShippingOption={selectedShippingOption} />
        <MarketingOptInCheckbox />
      </div>

      <div className={styles.actionContainer}>
        <ActionContainer>
          <Button
            onClick={(evt) => {
              evt.preventDefault();
              handlePurchase(email);
            }}
            disabled={purchaseMutationLoading || threeDSecurePending}
          >
            {t('place_order')}
          </Button>
        </ActionContainer>

        <ConfirmCheckoutDisclaimer />
      </div>
      {error && <ErrorMessage errors={[error]} errorID={errorID} />}
      {threeDSecureActionToken && stripeReady && (
        <FiRecurlyProvider>
          <ThreeDSecureAction
            actionTokenId={threeDSecureActionToken}
            onToken={onThreeDSecureSuccess}
            onError={onThreeDSecureError}
          />
        </FiRecurlyProvider>
      )}
    </div>
  );
}
